import React, {memo} from 'react';
import {Handle, Position, useStore} from 'reactflow';

import {Box, List, ListItem, ListItemIcon, ListItemText, Stack, Tooltip, Typography} from "@mui/material";
import {
  IconBroadcast,
  IconCornerRightDownDouble, IconLock, IconSitemap, IconWand,
} from "@tabler/icons-react";
import {
  getPalette,
  hexToRgba,
  iconChipSx,
  nodeHeaderSx,
  nodeKickerSx,
  nodeShellSx,
  nodeSurfaceSx,
  nodeTitleSx,
  sectionSx,
} from "./nodeTheme";


function CustomNode({id, data, selected}) {
  const {name, type, icon, greet, node} = data;
  var steps = data.steps || [];
  const totalSteps = steps?.length;

  const nextRoute = steps?.find(item => item?.type === "next");
  const noMatchRoute = steps?.find(item => item?.type === "no_match");
  const failRoute = steps?.find(item => item?.type === "fail");
  steps = steps?.filter(item => !["next", "no_match", "fail", "data"].includes(item?.type))
  const isProtected = node?.data?.data_protection || false;

  const hasSteps = steps?.length > 0;
  const palette = getPalette(totalSteps === 0 ? "warning" : "custom");

  // Which handles on this node actually have an edge attached. Connected handles
  // render in full accent colour; unconnected ones keep the soft header tint.
  const {targetConnected, sourceHandles} = useStore(
    (s) => {
      let t = false;
      const src = [];
      s.edges.forEach((e) => {
        if (e.target === id) t = true;
        if (e.source === id) src.push(e.sourceHandle || "");
      });
      return {targetConnected: t, sourceHandles: src.join("|")};
    },
    (a, b) => a.targetConnected === b.targetConnected && a.sourceHandles === b.sourceHandles,
  );
  const connectedSources = new Set(sourceHandles ? sourceHandles.split("|") : []);
  const isSourceConnected = (hid) => hid != null && connectedSources.has(hid);
  const handleClass = (base, connected) => `${base}${connected ? " is-connected" : ""}`.trim() || undefined;

  return (
    <Tooltip title={totalSteps === 0 ? "Please connect node with other node" : name}>
      <Box
        className={`node custom-node ${node?.data?.use_llm ? "magic-node" : ""}`}
        sx={{minWidth: "216px", maxWidth: "216px", ...nodeShellSx(palette, {selected})}}>

        {/* target dot – up top, level with the node icon */}
        <Handle type="target" position={Position.Left} id={id} style={{top: 16}}
                className={handleClass("", targetConnected)}/>

        <Box sx={nodeSurfaceSx}>
          <Box sx={{
            ...nodeHeaderSx(palette),
            ...sectionSx({first: true, last: !greet && !hasSteps}),
            position: "relative",
          }}>
            <Box sx={iconChipSx(palette)}>
              {icon || <IconSitemap size={12} stroke={1.8}/>}
            </Box>
            <Box sx={{minWidth: 0, flex: 1}}>
              <Typography component="span" sx={nodeKickerSx(palette)}>
                {(type || "step").toString().replaceAll("_", " ")}
              </Typography>
              <Typography noWrap sx={nodeTitleSx()}>{name}</Typography>
            </Box>
            <Stack direction={"row"} gap={0.5} sx={{position: "absolute", top: 3, right: 10}}>
              {isProtected &&
                <Tooltip title={"Data Protection"}><IconLock stroke={1.8} size={11} color={palette.accent}/></Tooltip>}
              {node?.data?.use_llm &&
                <Tooltip title={"Use LLM Booster"}><IconWand stroke={1.8} size={11} color={palette.accent}/></Tooltip>}
              {node?.data?.global_intents_included &&
                <Tooltip title={"Include Global Intents"}><IconBroadcast stroke={1.8} size={11}
                                                                        color={palette.accent}/></Tooltip>}
            </Stack>

            {/* static route dots – next / no_match / fail, tight stack level with the icon row */}
            <Handle type="source" position={Position.Right}
                    className={handleClass("static next", isSourceConnected(nextRoute?.id))}
                    style={{top: 8}} id={nextRoute?.id}/>
            <Handle type="source" position={Position.Right}
                    className={handleClass("static no-match", isSourceConnected(noMatchRoute?.id))}
                    style={{top: 17}} id={noMatchRoute?.id}/>
            <Handle type="source" position={Position.Right}
                    className={handleClass("static fail", isSourceConnected(failRoute?.id))}
                    style={{top: 26}} id={failRoute?.id}/>
          </Box>

          {greet && (
            <Box
              className={"greet-message"}
              sx={{
                px: 1.25,
                py: 0.75,
                bgcolor: "#FFFFFF",
                borderBottom: hasSteps ? `1px solid ${palette.border}` : "none",
                ...sectionSx({last: !hasSteps}),
              }}>
              <Tooltip
                title={<span style={{fontSize: 13, fontFamily: "inherit"}}>{greet || <em>No greet message</em>}</span>}
              >
                <Typography noWrap sx={{fontSize: 9, color: "#475569", fontStyle: greet ? "normal" : "italic"}}>
                  {greet || "No greet message"}
                </Typography>
              </Tooltip>
            </Box>
          )}

          {hasSteps && (
            <Box className="custom-node__body" sx={{background: "#FFFFFF", ...sectionSx({last: true})}}>
              <List sx={{p: 0, m: 0}}>
                {steps.filter(step => step !== null)
                  .map((step, index) => {
                    const isLast = index === steps.length - 1;
                    return <ListItem
                      key={step?.id}
                      sx={{
                        position: "relative",
                        px: 1,
                        py: 0.4,
                        m: 0,
                        borderBottom: isLast ? 0 : `1px solid ${palette.border}`,
                        transition: "background .15s ease",
                        "&:hover": {background: hexToRgba(palette.accent, 0.06)},
                        ...(isLast && sectionSx({last: true})),
                      }}
                      className="custom-node__select"
                    >
                      <ListItemIcon sx={{minWidth: 20, color: palette.accent}}>
                        {step?.icon || <IconCornerRightDownDouble size={12}/>}
                      </ListItemIcon>
                      <ListItemText sx={{p: 0, m: 0}}>
                        <Typography
                          sx={{
                            flex: 1,
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            fontWeight: 500,
                            fontSize: 8.5,
                            color: "#475569",
                          }}
                          align="left"
                        >
                          {step?.secondary}
                        </Typography>
                      </ListItemText>
                      {/* dynamic route dot – on the right border, aligned to this row */}
                      <Handle type="source" position={Position.Right} id={step?.id}
                              className={handleClass("dynamic", isSourceConnected(step?.id))}/>
                    </ListItem>
                  })}
              </List>
            </Box>
          )}
        </Box>
      </Box>
    </Tooltip>
  )
}

export default memo(CustomNode);
